import React, { useContext, useEffect } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { calculateYAxisConfig } from '../../helpers/chartHelpers';
import { ToolContext } from '../../../../../core/context/ToolContext';
import { rqx } from '../../../../../core/request/API';
import { env } from '../../../../../core/Env';
import { getDateRangeByFilter, shouldFetchForFilter } from '../../helpers/dateRangeHelper';
import { SnakeLoader } from '../../../../../core/loader/SnakeLoader';
import ChartFilter from '../../helpers/chartFilter';
import { generateDateRangeIntervals } from '../../helpers/dateRangeInterval';
import { fetchInstagramMessaging } from '../../helpers/metricHelpers';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Filler
);

export const MessagingChart = () => {
    const { tool_state, social_media_insight_state } = useContext(ToolContext)
    const { userId, __SESSION } = tool_state
    const platform = social_media_insight_state.connectedPlatform.data;

    const listView = async () => {
        if (platform == '' || social_media_insight_state.showProfilesModal.data) return;
        try {
            social_media_insight_state.messagingLoading.set(true)
            const dateRange = getDateRangeByFilter(social_media_insight_state.messagingFilter.data, social_media_insight_state.messagingCustomRange.data);
            let resMessaging = [];
            if (platform == 'facebook') {
                if (!social_media_insight_state.accessToken.data) {
                    console.error("No access token available in selected account");
                    social_media_insight_state.messagingData.set([]);
                    return;
                }
                resMessaging = await rqx.fbGraph(
                    `${social_media_insight_state.selectedProfileId.data}/insights?metric=page_messages_new_conversations_unique&period=day&since=${dateRange.start_date_unix}&until=${dateRange.end_date_unix}`,
                    social_media_insight_state.accessToken.data
                );
            } else if (platform == 'instagram') {
                resMessaging = await fetchInstagramMessaging({
                    accountId: social_media_insight_state.instagramAccountId.data,
                    accessToken: social_media_insight_state.instagramAccessToken.data,
                    start_date: dateRange.start_date_unix,
                    end_date: dateRange.end_date_unix,
                });
            }

            // Process the data based on platform
            let messagingByDate = [];

            if (platform == 'facebook') {
                const insightsData = resMessaging?.data?.[0]?.values || [];

                messagingByDate = insightsData.map(item => ({
                    date: dayjs(item.end_time).format('YYYY-MM-DD'),
                    value: item.value || 0
                }));
            } else if (platform == 'instagram') {
                const messagesDataMap = {};
                const conversations = Array.isArray(resMessaging?.data)
                    ? resMessaging.data
                    : Array.isArray(resMessaging)
                        ? resMessaging
                        : [];

                conversations.forEach(conversation => {
                    const updatedTime = conversation.updated_time?.slice(0, 10); // 'YYYY-MM-DD'
                    if (updatedTime) {
                        if (!messagesDataMap[updatedTime]) {
                            messagesDataMap[updatedTime] = 0;
                        }
                        messagesDataMap[updatedTime] += 1;
                    }
                });

                messagingByDate = Object.entries(messagesDataMap).map(([date, count]) => ({
                    date,
                    value: count
                }));
            }

            const resMessagingData = generateDateRangeIntervals(
                dateRange.start_date,
                dateRange.end_date,
                messagingByDate,
                'message_count'
            );
            social_media_insight_state.messagingData.set(resMessagingData);
        } catch (error) {
            console.error('Error fetching social media insights:', error);
            social_media_insight_state.messagingData.set([]);
        } finally {
            social_media_insight_state.messagingLoading.set(false)
        }
    }

    useEffect(() => {
        if (shouldFetchForFilter(social_media_insight_state.messagingFilter.data, social_media_insight_state.messagingCustomRange.data)) {
            listView();
        }
    }, [social_media_insight_state.messagingFilter.data, social_media_insight_state.messagingCustomRange.data, social_media_insight_state.urlCode.data, platform]);

    if (!userId?.data) {
        return null;
    }

    const messagingData = social_media_insight_state?.messagingData?.data || [];
    const labels = messagingData.map((item) => item.date);
    const values = messagingData.map(item => item.value || 0);
    const yAxisConfig = calculateYAxisConfig(values);

    const data = {
        labels: labels,
        datasets: [
            {
                label: 'Messages',
                type: 'bar',
                data: values,
                backgroundColor: 'rgba(99, 102, 241, 0.08)',
                barThickness: 30,
                borderRadius: 0,
                borderSkipped: false,
                hoverBackgroundColor: 'rgba(99, 102, 241, 0.08)',
            },
            {
                label: 'Growth',
                type: 'line',
                fill: true,
                data: values,
                borderColor: '#978FED',
                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                tension: 0.4,
                pointRadius: 0,
                pointHoverRadius: 6,
                pointBackgroundColor: '#6366F1',
                pointHoverBackgroundColor: '#6366F1',
                pointBorderColor: '#FFFFFF',
                pointBorderWidth: 2,
                borderWidth: 2,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                grid: {
                    display: false,
                    drawBorder: false,
                },
                ticks: {
                    color: '#666',
                    font: {
                        size: 12,
                    },
                },
                border: {
                    display: false,
                }
            },
            y: {
                ...yAxisConfig,
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.07)',
                    drawBorder: false,
                    lineWidth: 1,
                    borderDash: [5, 5],
                },
                border: {
                    display: false,
                }
            },
        },
        plugins: {
            tooltip: {
                backgroundColor: '#061D22',
                padding: 12,
                cornerRadius: 18,
                displayColors: false,
                titleFont: {
                    size: 0
                },
                bodyFont: {
                    size: 14,
                    weight: '600'
                },
                callbacks: {
                    title: () => '',
                    label: (context) => {
                        const label = context.dataset.label;
                        const value = context.parsed.y;
                        return `${value} ${label.toLowerCase()}`;
                    },
                },
                position: 'nearest',
                intersect: false,
                mode: 'index',
            },
            legend: {
                display: false,
            },
        },
        interaction: {
            intersect: false,
            mode: 'index',
        },
        layout: {
            padding: {
                left: 0,
                right: 0
            }
        }
    };

    return (
        <Box mb={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box fontSize={18} fontWeight={700} mb={2}>
                    Messaging
                </Box>
                <Box display="flex" gap={2}>
                    <ChartFilter
                        filterValue={social_media_insight_state.messagingFilter.data}
                        setFilterValue={social_media_insight_state.messagingFilter.set}
                        customRange={social_media_insight_state.messagingCustomRange.data}
                        setCustomRange={social_media_insight_state.messagingCustomRange.set}
                        // minDate={dayjs().subtract(2, 'year').toDate()}
                        maxDate={dayjs().toDate()}
                    />
                </Box>
            </Box>
            <div style={{ height: '300px', width: '100%' }}>
                {social_media_insight_state.messagingLoading.data ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%', fontSize: '14px', fontWeight: '600', color: '#666' }}>
                        <SnakeLoader size="1rem" bg="#11783C" distance="0.3rem" />
                    </ Box>
                ) :
                    (messagingData.length > 0 && messagingData.find(m => m.value > 0)) ? (
                        <Line data={data} options={options} />
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%', fontSize: '14px', fontWeight: '600', color: '#666' }}>
                            No data available
                        </Box>
                    )}
            </div>
        </Box>
    );
};